// Utils
import {
    createCustomEqualSmartSelector,
    createDeepSelector,
    createSmartSelector,
} from '../../utils/milanoteReselect/milanoteReselect';

// Selectors
import { getElements } from './elementsSelector';
import { getDeepChildrenModCache } from '../cache/deepChildrenModCacheSelector';
import { calculateElementGraphSelectorData } from '../../../common/elements/utils/elementGraphSelectorUtils';

// Board hierarchies
import { getBoardHierarchiesMapSelector } from '../board/hierarchy/boardHierarchiesSelector';
import { getBoardHierarchiesParentGraph } from '../board/hierarchy/boardHierarchyGraphUtil';
import { areBoardHierarchiesInitialised } from '../../app/initialisation/initialisationSelector';

// The graph should only be calculated once per render
// The graph should only be recalculated if the deepChildMod changes
const elementGraphEqualityChecker = (
    [prevElements, prevDeepChildrenModCache],
    [nextElements, nextDeepChildrenModCache],
) => prevDeepChildrenModCache === nextDeepChildrenModCache;

const createCustomElementChildrenSelector = createCustomEqualSmartSelector(elementGraphEqualityChecker);

/**
 * This is executed only once for every time the state slice has changed, and then its result
 * is shared between the graph selectors so that they don't need to perform very similar calculations again.
 */
const elementGraphDataSelector = createCustomElementChildrenSelector(
    getElements,
    getDeepChildrenModCache,
    calculateElementGraphSelectorData,
);

// TOP DOWN
/**
 * This selector gets the complete element graph of all elements currently in the client store.
 * This maps an element ID to its immediate children.
 *
 * NOTE: The elementGraphDataSelector selector will always return a new element graph when it executed, so there's
 *          no point using a selector
 */
export const elementGraphSelector = (state) => elementGraphDataSelector(state).elementGraph;

export const aliasIdToPhysicalIdMapSelector = createSmartSelector(
    elementGraphDataSelector,
    (elementGraphData) => elementGraphData.aliasIdToPhysicalIdMap,
);

// BOTTOM UP
/**
 * This is a map of element IDs to their physical parent ID.
 * It basically works as an index to speed up the retrieval of an element's parent ID and ancestors.
 *
 * NOTE: The elementGraphDataSelector selector will always return a new parentIdMap when it executed, so there's
 *          no point using a selector
 */
export const parentIdMapSelector = (state) => elementGraphDataSelector(state).parentIdMap;

/**
 * This maps an element ID to its visible descendants.
 * Visible descendants are all elements that would be visible when viewing that element.
 * So the children of columns, task lists, tasks etc will be associated to the parent board.
 *
 * NOTE: This might not change occasionally, but it would require a deep equals comparison which would take
 *          more time than it would be worth.  Instead - perform a shallow comparison on the individual element's entry
 */
export const boardVisibleElementGraphSelector = (state) => elementGraphDataSelector(state).boardVisibleElementGraph;

/**
 * Maps an element ID to its ancestor board ID.
 *
 * NOTE: The elementGraphDataSelector selector will always return a new parentIdMap when it executed, so there's
 *          no point using a selector
 */
export const elmentIdToAncestorBoardIdMapSelector = (state) =>
    elementGraphDataSelector(state).elementIdToAncestorBoardIdMap;

/**
 * Maps a column ID to its parent board ID.
 */
export const columnIdToParentBoardIdMapSelector = createSmartSelector(
    elementGraphDataSelector,
    (elementGraphData) => elementGraphData.columnIdToParentBoardIdMap,
);

export const elementIdToVirtualParentIdsMapSelector = createDeepSelector(
    elementGraphDataSelector,
    (elementGraphData) => elementGraphData.elementIdToVirtualParentIdsMap,
);

// BOARD SUMMARY GRAPHS
const EMPTY_OBJECT = {};

// This uses the board summaries instead of the element collection as the board summaries
// contains every board in a user's workspace
// It returns a parent board graph for every BOARD.  This is useful for doing bottom up
// traversals, like we do in the activity
export const fullVirtualBoardParentGraphSelector = createDeepSelector(
    areBoardHierarchiesInitialised,
    getBoardHierarchiesMapSelector,
    columnIdToParentBoardIdMapSelector,
    elementIdToVirtualParentIdsMapSelector,
    (isReady, boardHierarchies, columnIdToParentBoardIdMap, elementIdToVirtualParentIdsMap) => {
        if (!isReady) return EMPTY_OBJECT;

        return getBoardHierarchiesParentGraph(
            boardHierarchies,
            columnIdToParentBoardIdMap,
            elementIdToVirtualParentIdsMap,
        );
    },
);
